import React, { useEffect, useState, useRef } from "react";
import ReactCSSTransitionReplace from "react-css-transition-replace";
import { StaticImage } from "gatsby-plugin-image";
import { keyframes } from "@emotion/react";
import { mq } from "../util";

const generateQueue = (assets, caches) => {
  return assets.map(async ({ url, type }) => {
    if (type === "video") {
      return fetch(url)
        .then(response => response.blob())
        .then(response => {
          caches.push(response);
        })
        .catch(() => null);
    }

    return new Promise((resolve, _reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        caches.push(img);
        resolve();
      };
      img.onerror = () => resolve();
    });
  });
};

const Loading = ({ assets }) => {
  const progressCounterRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const Html = document.getElementsByTagName("html")[0];
    Html.style.overflowY = "hidden";

    const randomStr = Math.random().toString(32).slice(2) + Date.now();
    window.preloadAssetsData = window.preloadAssetsData ?? {};
    window.preloadAssetsData[randomStr] = [];

    const loadingQueue = generateQueue(assets, window.preloadAssetsData[randomStr]);

    (async () => {
      await Promise.all(loadingQueue);
      clearInterval(progressCounterRef.current);
      delete Html.style.overflowY;
      setProgress(100);
      setLoading(false);
    })();

    return () => {
      delete window.preloadAssetsData?.[randomStr];
    };
  }, [assets]);

  useEffect(() => {
    progressCounterRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 98) {
          clearInterval(progressCounterRef.current);
          return prev;
        }
        return prev + 1;
      });
    }, 10);

    return () => clearInterval(progressCounterRef.current);
  }, []);

  return (
    <ReactCSSTransitionReplace
      transitionName="cross-fade"
      transitionEnterTimeout={500}
      transitionLeaveTimeout={500}>
      {loading && (
        <div
          key="loading"
          css={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 50,
            display: "grid",
          }}>
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gridArea: "1/1",
            }}>
            <div
              css={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: -1,
                backgroundColor: "#1f2026",
              }}
            />
            <div
              css={mq({
                minHeight: [200, 500],
                position: "relative",
                display: "grid",
                rowGap: [70, 100, 120],
                alignContent: "center",
                zIndex: 5,
              })}>
              <div
                css={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}>
                <StaticImage
                  src="../assets/images/loading/logo.png"
                  alt="logo"
                  css={mq({
                    width: [260, 400, 540],
                  })}
                />
                <div
                  css={mq({
                    marginTop: [-32, -47, -65],
                    transform: "rotateX(80deg)",
                  })}>
                  <Ripple />
                  <Ripple
                    css={{
                      animationDelay: "0.8s",
                    }}
                  />
                </div>
              </div>
              <div
                css={mq({
                  fontSize: [14, 18, 24],
                  textAlign: "center",
                  letterSpacing: "4px",
                  textShadow: "0px 0px 10.92px rgba(174, 162, 168, 0.75)",
                })}>
                <span>
                  Loading<i>.</i>
                  <i>.</i>
                  <i>.</i>
                </span>
                <span>{progress}%</span>
              </div>
            </div>
          </div>
          <StaticImage
            src="../assets/images/loading/loading-cover.png"
            alt="loading background"
            css={{
              gridArea: "1/1",
            }}
          />
        </div>
      )}
    </ReactCSSTransitionReplace>
  );
};

const Ripple = props => (
  <span
    css={mq({
      boxShadow: "0 0 5px 3px rgba(255, 255, 255, .4)",
      position: "fixed",
      top: "0",
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      animationName: [rippleMini, rippleMob, ripple],
      animationDuration: "4s",
      animationIterationCount: "infinite",
      animationTimingFunction: "linear",
      transform: "translateX(-50%)",
    })}
    {...props}
  />
);

const ripple = keyframes`
    from {
        border: 6px solid #fff;
    }

    to {
        border: 0px solid #fff;
        width: 400px;
        height: 400px;
        top: -120px;
        opacity: 0;
    }
`;

const rippleMob = keyframes`
  from {
    border: 4px solid #fff;
  }

  to {
    border: 0px solid #fff;
    width: 296px;
    height: 296px;
    top: -88px;
    opacity: 0;
  }
`;

const rippleMini = keyframes`
  from {
    border: 3px solid #fff;
  }

  to {
    border: 0px solid #fff;
    width: 193px;
    height: 193px;
    top: -58px;
    opacity: 0;
  }
`;

export default Loading;
